/**
 * PostHog product-analytics wrapper (OMN-360).
 *
 * Privacy posture (decided for OmniFlyer's EU club/school users):
 *  - Opt-in only: PostHog is initialised with capturing OFF by default and only
 *    starts once the user accepts via the consent banner. Declining persists an
 *    explicit opt-out. See AnalyticsConsentBanner / AnalyticsProvider.
 *  - Pseudonymous: users are identified by their auth uid + role and grouped by
 *    organization id only — never email or other PII.
 *  - No session replay.
 *
 * Every function is a safe no-op when PostHog isn't configured (no key) or not
 * yet initialised, so call sites never need to guard.
 */
import posthog from 'posthog-js';

let initialized = false;

// Our own explicit-consent flag. We can't use PostHog's has_opted_out_capturing()
// to detect "user decided", because opt_out_capturing_by_default:true makes it
// report opted-out before any actual choice — which would hide the banner.
const CONSENT_KEY = 'omniflyer_analytics_consent';

function readConsent(): 'accepted' | 'declined' | null {
  try {
    return localStorage.getItem(CONSENT_KEY) as 'accepted' | 'declined' | null;
  } catch {
    return null;
  }
}

function writeConsent(value: 'accepted' | 'declined'): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* storage unavailable — consent simply won't persist */
  }
}

const getKey = () => process.env.REACT_APP_POSTHOG_KEY;
const getHost = () => process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

/** True when a PostHog project key is configured — i.e. analytics can run at all. */
export function isAnalyticsConfigured(): boolean {
  return !!getKey();
}

export function initAnalytics(): void {
  if (initialized || !getKey()) return;
  posthog.init(getKey() as string, {
    api_host: getHost(),
    ui_host: 'https://us.posthog.com',
    autocapture: true,
    capture_pageview: false, // SPA pageviews are captured manually on route change
    capture_pageleave: true,
    disable_session_recording: true, // privacy: no replay
    person_profiles: 'identified_only',
    // Consent gate: nothing is captured until the user explicitly opts in.
    opt_out_capturing_by_default: true,
  });
  // Restore a prior "accepted" choice: opt_out_capturing_by_default keeps
  // capturing off until we explicitly opt back in on load.
  if (readConsent() === 'accepted') posthog.opt_in_capturing();
  initialized = true;
}

/** Whether the user has made an explicit consent choice (accept or decline). */
export function consentDecided(): boolean {
  return readConsent() !== null;
}

/** Whether the user has opted in — i.e. capturing/identify should run. */
export function analyticsOptedIn(): boolean {
  return readConsent() === 'accepted';
}

export function acceptAnalytics(): void {
  writeConsent('accepted');
  if (initialized) posthog.opt_in_capturing();
}

export function declineAnalytics(): void {
  writeConsent('declined');
  if (initialized) posthog.opt_out_capturing();
}

export function captureEvent(name: string, props?: Record<string, unknown>): void {
  if (initialized) posthog.capture(name, props);
}

export function capturePageview(path: string): void {
  if (initialized) posthog.capture('$pageview', { path });
}

/** Identify pseudonymously: uid + role only, never email/PII. */
export function identifyUser(uid: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.identify(uid, props);
  // identify() only $sets properties on the first anon→identified transition;
  // setPersonProperties keeps role current for already-identified return visits.
  if (props) posthog.setPersonProperties(props);
}

export function groupOrg(orgId: string, props?: Record<string, unknown>): void {
  if (initialized) posthog.group('organization', orgId, props);
}

/** Clear identity on logout so the next user starts fresh. */
export function resetAnalytics(): void {
  if (initialized) posthog.reset();
}

/** Test-only: reset module state between tests. */
export function __resetForTests(): void {
  initialized = false;
}
